import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

export type SearchStateEntries = Array<[key: string, value: any]>

const pendingDebouncedTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
let pendingDebouncedState: string | null = null
let optimisticSearchState: URLSearchParams | null = null

interface UseSearchStateOptions {
  defaultParams?: URLSearchParams
  debouncedKeys?: string[]
  debounceTimeout?: number
}

export function useSearchState({ defaultParams, debouncedKeys = [], debounceTimeout }: UseSearchStateOptions) {
  const [urlSearchParams, setUrlSearchParams] = useSearchParams(defaultParams)
  const [displayState, setDisplayState] = useState(() => new URLSearchParams(urlSearchParams))

  const displayStateRef = useRef(displayState)
  const urlSearchParamsRef = useRef(urlSearchParams)

  displayStateRef.current = displayState
  urlSearchParamsRef.current = urlSearchParams

  useEffect(() => {
    const urlString = urlSearchParams.toString()
    const displayString = displayState.toString()

    if (urlString === displayString) {
      if (pendingDebouncedState && pendingDebouncedState !== urlString) {
        return
      }

      optimisticSearchState = new URLSearchParams(urlSearchParams)
      pendingDebouncedState = null
      return
    }

    if (pendingDebouncedState === displayString) {
      return
    }

    const nextDisplayState = new URLSearchParams(urlSearchParams)
    optimisticSearchState = nextDisplayState
    displayStateRef.current = nextDisplayState
    setDisplayState(nextDisplayState)
  }, [urlSearchParams])

  const touchedParamsCount = useMemo(() => {
    if (!defaultParams) {
      return new Set(displayState.keys()).size
    }

    let count = 0
    const currentKeys = new Set(displayState.keys())
    const defaultKeys = new Set(defaultParams.keys())
    const allKeys = new Set([...currentKeys, ...defaultKeys])

    for (const key of allKeys) {
      const currentValues = displayState.getAll(key).sort()
      const defaultValues = defaultParams.getAll(key).sort()

      const hasChanged =
        currentValues.length !== defaultValues.length || currentValues.some((val, idx) => val !== defaultValues[idx])

      if (hasChanged) {
        count++
      }
    }

    return count
  }, [displayState, defaultParams])

  const updateSearchState = useCallback(
    (...entries: SearchStateEntries) => {
      const newDisplayState = new URLSearchParams(optimisticSearchState ?? displayStateRef.current)

      for (const [key, value] of entries) {
        if (value === undefined || value === null || value === '') {
          newDisplayState.delete(key)
          continue
        }

        if (Array.isArray(value)) {
          newDisplayState.delete(key)
          for (const item of value) {
            newDisplayState.append(key, String(item))
          }
        } else {
          newDisplayState.set(key, String(value))
        }
      }

      const immediateKeys = []
      const debouncedKeysList = []

      for (const [key] of entries) {
        if (debouncedKeys.includes(key)) {
          debouncedKeysList.push(key)
        } else {
          immediateKeys.push(key)
        }
      }

      if (immediateKeys.length > 0) {
        pendingDebouncedTimeouts.forEach((timeout) => clearTimeout(timeout))
        pendingDebouncedTimeouts.clear()
        pendingDebouncedState = null
      }

      if (displayStateRef.current.toString() !== newDisplayState.toString()) {
        displayStateRef.current = newDisplayState
        setDisplayState(newDisplayState)
      }
      optimisticSearchState = newDisplayState

      if (immediateKeys.length > 0) {
        if (urlSearchParamsRef.current.toString() !== newDisplayState.toString()) {
          urlSearchParamsRef.current = newDisplayState
          setUrlSearchParams(new URLSearchParams(newDisplayState), { replace: true })
        }
      }

      if (debouncedKeysList.length > 0) {
        pendingDebouncedState = newDisplayState.toString()
      }

      for (const key of debouncedKeysList) {
        const existingTimeout = pendingDebouncedTimeouts.get(key)
        if (existingTimeout) {
          clearTimeout(existingTimeout)
        }

        const displayStateSnapshot = new URLSearchParams(newDisplayState)
        const displayStateSnapshotString = displayStateSnapshot.toString()

        const timeout = setTimeout(() => {
          if (pendingDebouncedState !== displayStateSnapshotString) {
            pendingDebouncedTimeouts.delete(key)
            return
          }

          urlSearchParamsRef.current = displayStateSnapshot
          optimisticSearchState = displayStateSnapshot
          setUrlSearchParams(displayStateSnapshot, { replace: true })
          pendingDebouncedTimeouts.delete(key)
        }, debounceTimeout)

        pendingDebouncedTimeouts.set(key, timeout)
      }
    },
    [debouncedKeys, debounceTimeout, setUrlSearchParams]
  )

  const resetSearchParam = useCallback(
    (key: string) => {
      pendingDebouncedTimeouts.forEach((timeout) => clearTimeout(timeout))
      pendingDebouncedTimeouts.clear()

      const newDisplayState = new URLSearchParams(optimisticSearchState ?? displayStateRef.current)
      newDisplayState.delete(key)
      displayStateRef.current = newDisplayState
      optimisticSearchState = newDisplayState
      pendingDebouncedState = null
      setDisplayState(newDisplayState)

      urlSearchParamsRef.current = newDisplayState
      setUrlSearchParams(new URLSearchParams(newDisplayState), { replace: true })
    },
    [setUrlSearchParams]
  )

  const clearSearchState = useCallback(() => {
    pendingDebouncedTimeouts.forEach((timeout) => clearTimeout(timeout))
    pendingDebouncedTimeouts.clear()

    const newState = new URLSearchParams(defaultParams)
    displayStateRef.current = newState
    urlSearchParamsRef.current = newState
    optimisticSearchState = newState
    pendingDebouncedState = null
    setDisplayState(newState)
    setUrlSearchParams(newState, { replace: true })
  }, [defaultParams, setUrlSearchParams])

  return {
    searchState: displayState,
    touchedParamsCount,
    updateSearchState,
    resetSearchParam,
    clearSearchState,
  } as const
}
